import moment from "moment";
import "moment/locale/es";
import { capitalize } from "@/utils/string";

/**
 * Construye la etiqueta de período a partir de un rango de índices del array
 * `monthHeaders` (cada header tiene formato "MMM YYYY", ej: "Abr 2025").
 *
 * Salidas posibles:
 *  - Mismo mes y año:        "Abr 2025"
 *  - Mismo año, distinto mes: "Ene - Abr 2025"
 *  - Cruza año:               "May 2024 - Abr 2025"
 *
 * Distinto de `formatPeriodLabel(period, filterPeriod)` en
 * `store/financialReports/utils/dateRange.js`, que formatea una sola fecha
 * según granularidad mes/quarter/año.
 */
export function formatRangeFromIndices(monthHeaders, indices) {
    if (!indices || !indices.length) return "-";

    const firstStr = monthHeaders[indices[0]];
    const lastStr = monthHeaders[indices[indices.length - 1]];

    const firstMonth = firstStr.split(" ")[0];
    const firstYear = firstStr.split(" ").pop();
    const lastMonth = lastStr.split(" ")[0];
    const lastYear = lastStr.split(" ").pop();

    const capFirst = capitalize(firstMonth);
    const capLast = capitalize(lastMonth);

    if (firstMonth === lastMonth && firstYear === lastYear) {
        return `${capFirst} ${firstYear}`;
    }
    if (firstYear === lastYear) {
        return `${capFirst} - ${capLast} ${lastYear}`;
    }
    return `${capFirst} ${firstYear} - ${capLast} ${lastYear}`;
}

// ----- Helpers para summary cards (rango definido por fechas ISO) -----

/**
 * Construye la etiqueta de período a partir de dos fechas ISO (YYYY-MM-DD).
 * Variante de `formatRangeFromIndices` que no depende de `monthHeaders`.
 */
export function formatRangeLabel(startIso, endIso) {
    const start = moment(startIso);
    const end = moment(endIso);
    const startMonth = capitalize(start.format("MMM").replace(".", ""));
    const startYear = start.format("YYYY");
    const endMonth = capitalize(end.format("MMM").replace(".", ""));
    const endYear = end.format("YYYY");

    if (startMonth === endMonth && startYear === endYear) {
        return `${startMonth} ${startYear}`;
    }
    if (startYear === endYear) {
        return `${startMonth} - ${endMonth} ${endYear}`;
    }
    return `${startMonth} ${startYear} - ${endMonth} ${endYear}`;
}

/**
 * Convierte una fecha ISO al formato usado por monthHeaders ("MMM YYYY", ej "abr 2025").
 */
export function dateToHeaderLabel(iso) {
    return moment(iso).format("MMM YYYY").replace(".", "");
}

/**
 * Etiqueta de un bucket según su granularidad: "abr 2025" para un mes,
 * "Q2 2025" para un trimestre y "2025" para un año. `iso` es el primer día
 * del bucket (como lo devuelve el análisis de Rentabilidad en
 * `evolution.buckets`).
 */
export function bucketToHeaderLabel(iso, period = "month") {
    // YTD: un bucket por año, recortado al mes de corte (como las columnas).
    if (period === "year" || period === "ytd") return moment(iso).format("YYYY");
    if (period === "quarter") return moment(iso).format("[Q]Q YYYY");
    return dateToHeaderLabel(iso);
}

/**
 * Cantidad de meses incluidos entre dos fechas ISO (ambos extremos inclusive).
 */
export function monthsBetween(startIso, endIso) {
    const start = moment(startIso).startOf("month");
    const end = moment(endIso).startOf("month");
    return end.diff(start, "months") + 1;
}

/**
 * Devuelve los índices de monthHeaders correspondientes al rango [startIso, endIso].
 * Retorna [] si alguno de los extremos no está presente.
 */
export function indicesForRange(monthHeaders, startIso, endIso) {
    const startIdx = monthHeaders.indexOf(dateToHeaderLabel(startIso));
    const endIdx = monthHeaders.indexOf(dateToHeaderLabel(endIso));
    if (startIdx === -1 || endIdx === -1) return [];
    const indices = [];
    for (let i = startIdx; i <= endIdx; i++) indices.push(i);
    return indices;
}

/**
 * Formatea un ratio (ej. 0.0856) como porcentaje ENTERO con redondeo usual
 * (half-up): 0.0856 → "9%", 0.235 → "24%". Devuelve "" si ratio es
 * null/undefined. Convención del sistema: todos los porcentajes de los tabs van
 * sin decimales (ver CLAUDE.md → Frontend).
 */
export function formatPercent(ratio) {
    if (ratio === null || ratio === undefined) return "";
    return `${Math.round(ratio * 100)}%`;
}

/**
 * Último mes ("YYYY-MM") del período al que pertenece la columna clickeada,
 * según la granularidad activa. El modal de análisis (modal 1) es mensual, así
 * que al clickear una celda de cualquier filtro se abre el último mes de ese
 * período:
 *  - month:   el propio mes ("Abr 2025" → 2025-04).
 *  - quarter: último mes del trimestre (Q1→Mar, Q2→Jun, Q3→Sep, Q4→Dic).
 *  - year:    Diciembre del año.
 *  - ytd:     el mes de corte (el mes de `endMonth`) de ese año.
 *
 * Cap: nunca pasa de `endMonth`. En un período EN CURSO (p.ej. el año actual en
 * Anual, cuyo Dic es futuro y sin data) cae al último mes con data (`endMonth`).
 * Devuelve "" si el label no parsea.
 */
export function periodLastMonth(label, filterPeriod, endMonth) {
    const end = moment(endMonth).endOf("month");
    let target;

    if (filterPeriod === "quarter") {
        const m = /Q(\d)\s+(\d{4})/i.exec(label);
        if (!m) return "";
        // Q1→mes índice 2 (Mar), Q2→5, Q3→8, Q4→11 (Dic).
        target = moment({ year: Number(m[2]), month: Number(m[1]) * 3 - 1, day: 1 });
    } else if (filterPeriod === "year" || filterPeriod === "ytd") {
        const y = (label.match(/\d{4}/) || [])[0];
        if (!y) return "";
        const month = filterPeriod === "ytd" ? end.month() : 11; // ytd: mes de corte; year: Dic
        target = moment({ year: Number(y), month, day: 1 });
    } else {
        const m = moment(label, "MMM YYYY", "es");
        if (!m.isValid()) return "";
        target = m;
    }

    target = target.endOf("month");
    if (target.isAfter(end)) target = end.clone();
    return target.format("YYYY-MM");
}
